import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";

// Material
import { makeStyles } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Chip from "@material-ui/core/Chip";
import Skeleton from "@material-ui/lab/Skeleton";

// Config
import colors from "src/config/colors";
import styles from "src/config/styles";
import useTranslate from "src/hooks/useTranslate";
import { useRecipients } from "src/context/RecipientsProvider";
import { statementItemUrl } from "src/config/apiConfig";

// Icons
import sailogo from "../../assets/logos/sai-logo.png";

// Actions
import { parseFetchOptions } from "src/state/user/actions";

// Assets
import pendingStatementIcon from "../../assets/icons/pendingStatementIcon.svg";

// Components
import Button from "../Button";
import Text from "../Text";
import ForceLogo from "./ForceLogo";
import { statementRequestSources, statementStatus } from "src/config/values";

const useStyles = makeStyles(theme => ({
  root: {
    border: styles.values.border,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    width: `calc(50% - ${theme.spacing(1)}px)`,
    marginBottom: theme.spacing(2),
    backgroundColor: colors.hintOfRed,
    alignItems: "flex-start",
    gap: theme.spacing(2)
  },
  cardContent: {
    paddingBottom: theme.spacing(2),
    gap: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    width: "100%"
  },
  forceWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%"
  },
  draftChip: {
    height: 20,
    fontSize: 8,
    borderRadius: 10,
    textTransform: "uppercase",
    backgroundColor: colors.emergencyHeader,
    color: colors.white,
    marginLeft: "auto",
    padding: theme.spacing(0.5, 1)
  },
  draftChipLabel: {
    padding: 0
  },
  storeImg: {
    display: "block",
    height: "27px"
  },
  saiColor: {
    color: `${colors.emergencyHeader} !important`
  },
  saiBackground: {
    backgroundColor: "#E7F9FC"
  },
  formTitle: {
    lineHeight: 1.1,
    color: colors.lockedCardText,
    display: "flex",
    alignItems: "center"
  },
  iconPending: {
    marginRight: "8px"
  },
  requiredText: {
    textTransform: "uppercase",
    color: colors.lockedCardText
  },
  contentText: {
    fontSize: "14px",
    color: colors.riverBed
  },
  button: {
    backgroundColor: colors.emergencyHeader,
    marginTop: "auto"
  }
}));

const PendingStatementItem = ({ onClick, statement }) => {
  const { data } = useSelector(s => s.caseDetail);
  const { loading, recipients } = useRecipients();
  const classes = useStyles();
  const getText = useTranslate();
  const dispatch = useDispatch();
  const [statementData, setStatementData] = useState(null);

  useEffect(() => {
    if (!statementData) {
      dispatch(
        parseFetchOptions(async options => {
          const response = await fetch(
            statementItemUrl(data.id, statement.id),
            {
              method: "GET",
              headers: options.headers
            }
          ).catch(() => {
            // no-op
          });

          if (response && response.ok) {
            const res = await response.json();
            setStatementData(res);
          }
        })
      );
    }
  }, [data.id, dispatch, statement.id]);

  if (loading || !statementData) {
    return (
      <Card component="article" variant="outlined" className={classes.root}>
        <CardContent className={classes.cardContent}>
          <Skeleton animation="wave" variant="rect" width="50%" />
          <Skeleton animation="wave" variant="rect" width="100%" />
          <Skeleton
            animation="wave"
            variant="rect"
            height="170px"
            width="100%"
          />
          <Skeleton animation="wave" variant="rect" height="40px" width="70%" />
        </CardContent>
      </Card>
    );
  }

  const isSai =
    statementData?.requestSource === statementRequestSources.SELF_ADMINISTERED;
  const isDraft = statement.state === statementStatus.DRAFT;

  const buttonTextKey = isSai
    ? {
        draft: "selfAdministeredInterviewsPage.continueButton",
        pending: "selfAdministeredInterviewsPage.completeNowButton"
      }
    : {
        draft: "common.prompts.continue",
        pending: "statementsPage.completeStatementButton"
      };

  const recipient =
    recipients &&
    recipients.length > 0 &&
    recipients.find(r => r.id === statement.organizationId);

  return (
    <Card
      component="article"
      variant="outlined"
      className={clsx(classes.root, {
        [classes.saiBackground]: isSai
      })}
    >
      <Box className={classes.cardContent}>
        <Box className={classes.forceWrapper}>
          {isSai && (
            <img className={classes.storeImg} src={sailogo} alt="SAI-logo" />
          )}
          {!isSai && recipient && (
            <>
              <ForceLogo
                src={recipient.logoUri}
                alt={recipient.name}
                color={recipient.colorHex}
              />
              <Text type="heading3" className={classes.organization}>
                {recipient.name}
              </Text>
            </>
          )}
          {isDraft && (
            <Chip
              label={getText("common.status.draft")}
              className={classes.draftChip}
              classes={{ label: classes.draftChipLabel }}
            />
          )}
        </Box>
        <Text
          type="heading2"
          className={clsx(classes.formTitle, {
            [classes.saiColor]: isSai
          })}
        >
          {!isSai && (
            <img
              src={pendingStatementIcon}
              alt=""
              className={classes.iconPending}
            />
          )}
          {statement.title}
        </Text>
        {!isSai && (
          <Text className={classes.requiredText} type="contentLabel">
            {getText("common.prompts.actionRequired")}
          </Text>
        )}
        {isSai ? (
          <Text
            type="body"
            className={classes.contentText}
            text="selfAdministeredInterviewsPage.draftDescription"
          />
        ) : (
          <Text
            type="body"
            className={classes.contentText}
            text="statementsPage.pendingDescription"
            values={{
              1:
                statementData?.requester?.fullName ??
                getText("common.valueLabels.unknown"),
              2: data.number
            }}
          />
        )}
      </Box>
      <Button
        variant="contained"
        regular
        disableElevation
        className={classes.button}
        color="primary"
        onClick={onClick}
      >
        {getText(buttonTextKey[isDraft ? "draft" : "pending"])}
      </Button>
    </Card>
  );
};

PendingStatementItem.propTypes = {
  onClick: PropTypes.func.isRequired,
  statement: PropTypes.shape({
    title: PropTypes.string,
    id: PropTypes.string,
    organizationId: PropTypes.string,
    state: PropTypes.string
  }).isRequired
};
export default React.memo(PendingStatementItem);
